import { useEffect, useState, useCallback, useMemo } from 'react'
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Grid,
  HStack,
  Input,
  Select,
  Textarea,
  VStack,
} from '@chakra-ui/react'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { apiClient } from '@/lib/api/client'

export type BrandModelFormProps = {
  brandModelId?: number
  onSuccess?: () => void
  onCancel?: () => void
}

type BrandOption = { id: number; name: string; categories?: Array<{ id: number; name: string }> }
type CategoryOption = { id: number; name: string }

const initialValues = {
  brand_id: '' as string | number,
  category_id: '' as string | number,
  name: '',
  slug: '',
  descriptions: '',
  active: true,
}

function slugify(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
}

/**
 * Brand Model form (e.g. Epson "L3110" under brand Epson, category Printer).
 * Maps to the "Model" dropdown in CreateAssetModal, which is filtered by
 * both the selected Brand and Category.
 */
export function BrandModelForm({ brandModelId, onSuccess, onCancel }: BrandModelFormProps) {
  const { showError, showSuccess } = useToastNotification()
  const [loading, setLoading] = useState(!!brandModelId)
  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState<Record<string, string[]>>({})
  const [form, setForm] = useState(initialValues)
  const [slugTouched, setSlugTouched] = useState(false)
  const [brandOptions, setBrandOptions] = useState<BrandOption[]>([])
  const [allCategoryOptions, setAllCategoryOptions] = useState<CategoryOption[]>([])
  const [optionsLoading, setOptionsLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    setOptionsLoading(true)
    Promise.all([
      apiClient.get('/master-data/brands', { params: { per_page: 500 } }),
      apiClient.get('/master-data/categories', { params: { per_page: 500 } }),
    ])
      .then(([brandsRes, categoriesRes]) => {
        if (cancelled) return
        const brandRows = Array.isArray(brandsRes.data?.data) ? brandsRes.data.data : []
        setBrandOptions(
          brandRows.map((b: { id: number; name?: string; categories?: string }) => {
            let categories: Array<{ id: number; name: string }> = []
            try {
              categories = b.categories ? JSON.parse(b.categories) : []
            } catch {
              categories = []
            }
            return { id: Number(b.id), name: String(b.name ?? b.id), categories }
          })
        )
        const categoryRows = Array.isArray(categoriesRes.data?.data) ? categoriesRes.data.data : []
        setAllCategoryOptions(
          categoryRows.map((c: { id: number; name?: string }) => ({ id: Number(c.id), name: String(c.name ?? c.id) }))
        )
      })
      .catch(() => {
        if (!cancelled) {
          setBrandOptions([])
          setAllCategoryOptions([])
        }
      })
      .finally(() => {
        if (!cancelled) setOptionsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const loadOne = useCallback(async () => {
    if (!brandModelId) return
    setLoading(true)
    setErrors({})
    try {
      const { data } = await apiClient.get(`/master-data/brand-models/${brandModelId}`)
      const r = data?.data ?? data
      setForm({
        brand_id: r.brand_id ?? '',
        category_id: r.category_id ?? '',
        name: r.name ?? '',
        slug: r.slug ?? '',
        descriptions: r.descriptions ?? '',
        active: r.active !== false,
      })
      setSlugTouched(true)
    } catch (e: unknown) {
      showError(e as Error)
    } finally {
      setLoading(false)
    }
  }, [brandModelId, showError])

  useEffect(() => {
    if (brandModelId) {
      loadOne()
    } else {
      setForm(initialValues)
      setSlugTouched(false)
    }
  }, [brandModelId, loadOne])

  // Category options scoped to the selected brand's declared categories;
  // falls back to the full category list while options are still loading
  // or if the brand has no categories recorded.
  const categoryOptionsForBrand = useMemo(() => {
    const brand = brandOptions.find((b) => String(b.id) === String(form.brand_id))
    if (brand?.categories && brand.categories.length > 0) {
      return brand.categories
    }
    return allCategoryOptions
  }, [brandOptions, allCategoryOptions, form.brand_id])

  const updateField = (field: keyof typeof form, value: string | number | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev }
        delete next[field]
        return next
      })
    }
  }

  const handleBrandChange = (value: string) => {
    setForm((prev) => ({ ...prev, brand_id: value, category_id: '' }))
  }

  const handleNameChange = (value: string) => {
    setForm((prev) => ({
      ...prev,
      name: value,
      slug: slugTouched ? prev.slug : slugify(value),
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrors({})
    setSaving(true)
    try {
      const payload = {
        brand_id: form.brand_id === '' ? null : Number(form.brand_id),
        category_id: form.category_id === '' ? null : Number(form.category_id),
        name: form.name.trim(),
        slug: (form.slug.trim() || slugify(form.name)).trim(),
        descriptions: form.descriptions.trim() || null,
        active: form.active,
      }
      if (brandModelId) {
        await apiClient.put(`/master-data/brand-models/${brandModelId}`, payload)
        showSuccess('Success', 'Brand model updated successfully')
      } else {
        await apiClient.post('/master-data/brand-models', payload)
        showSuccess('Success', 'Brand model created successfully')
      }
      onSuccess?.()
    } catch (err: unknown) {
      const res = (err as { response?: { data?: { errors?: Record<string, string[]> } } })?.response?.data
      if (res?.errors && typeof res.errors === 'object') {
        setErrors(res.errors)
      } else {
        showError(err as Error)
      }
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <Box py={8}>
        <Box as="span">Loading...</Box>
      </Box>
    )
  }

  return (
    <Box as="form" onSubmit={handleSubmit}>
      <VStack spacing={4} align="stretch">
        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Brand <Box as="span" color="red.500">*</Box>
          </FormLabel>
          <FormControl isInvalid={!!errors.brand_id?.length} isRequired>
            <Select
              value={form.brand_id}
              onChange={(e) => handleBrandChange(e.target.value)}
              placeholder={optionsLoading ? 'Loading...' : 'Select brand'}
              isDisabled={optionsLoading}
            >
              {brandOptions.map((b) => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </Select>
            <FormErrorMessage>{errors.brand_id?.[0]}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Category (Machine Type) <Box as="span" color="red.500">*</Box>
          </FormLabel>
          <FormControl isInvalid={!!errors.category_id?.length} isRequired>
            <Select
              value={form.category_id}
              onChange={(e) => updateField('category_id', e.target.value)}
              placeholder={!form.brand_id ? 'Select a brand first' : 'Select category'}
              isDisabled={!form.brand_id}
            >
              {categoryOptionsForBrand.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </Select>
            <FormHelperText>Only categories assigned to the selected brand are shown.</FormHelperText>
            <FormErrorMessage>{errors.category_id?.[0]}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Name <Box as="span" color="red.500">*</Box>
          </FormLabel>
          <FormControl isInvalid={!!errors.name?.length} isRequired>
            <Input
              value={form.name}
              onChange={(e) => handleNameChange(e.target.value)}
              placeholder="e.g. L3110, LaserJet Pro M404"
            />
            <FormErrorMessage>{errors.name?.[0]}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Slug</FormLabel>
          <FormControl isInvalid={!!errors.slug?.length}>
            <Input
              value={form.slug}
              onChange={(e) => {
                setSlugTouched(true)
                updateField('slug', slugify(e.target.value))
              }}
              placeholder="auto-generated from name"
            />
            <FormErrorMessage>{errors.slug?.[0]}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Description</FormLabel>
          <FormControl isInvalid={!!errors.descriptions?.length}>
            <Textarea
              value={form.descriptions}
              onChange={(e) => updateField('descriptions', e.target.value)}
              placeholder="Optional description"
              rows={3}
            />
            <FormErrorMessage>{errors.descriptions?.[0]}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="center">
          <FormLabel pt={2} fontWeight="medium" mb={0}>Active</FormLabel>
          <FormControl>
            <Checkbox
              isChecked={form.active}
              onChange={(e) => updateField('active', e.target.checked)}
              colorScheme="blue"
            >
              <Box as="span">Available for selection on store assets.</Box>
            </Checkbox>
          </FormControl>
        </Grid>

        <HStack spacing={4} justify="flex-end" pt={4}>
          {onCancel && (
            <Button variant="outline" onClick={onCancel} isDisabled={saving}>
              Cancel
            </Button>
          )}
          <Button type="submit" colorScheme="blue" isLoading={saving} loadingText={brandModelId ? 'Saving...' : 'Creating...'}>
            {brandModelId ? 'Save' : 'Create Brand Model'}
          </Button>
        </HStack>
      </VStack>
    </Box>
  )
}
